import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { MoreVertical, X, Users, Flag, Pencil, Trash2 } from "lucide-react";
import styles from "./homePage.module.scss";
import noImageUrl from "../../images/no-image.png";
import { convertToCloudFrontUrl } from "../../helper";
import AddedBy from "../../components/addedBy";

const formatReportDate = (dateStr) => {
  const date = new Date(dateStr);
  // A report with no date (or a malformed one) printed "Invalid Date".
  if (!dateStr || Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isMeaningfulReport = (report) => {
  if (!report || typeof report !== "object") return false;
  return ["userId", "reportedBy", "reportedEmail", "reason", "reportedAt"].some((key) => {
    const value = report[key];
    return value != null && value !== "";
  });
};

const getReportSummary = (totalUserCount, validReports) => ({
  totalUserCount,
  reportsCount: Array.isArray(validReports) ? validReports.length : 0,
});

const PostCard = (props) => {
  const { post, onEditSuccess, onDeleteSuccess, onClick, onStatusSuccess, totalUserCount } = props;

  const [showTooltip, setShowTooltip] = useState(false);
  const [isPostActive, setIsPostActive] = useState(true);
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);
  // A visibility save in flight — the switch ignores clicks until it answers.
  const [isSavingStatus, setIsSavingStatus] = useState(false);

  const reports = post.reports || post.reportData || [];
  const validReports = Array.isArray(reports) ? reports.filter(isMeaningfulReport) : [];
  const { reportsCount } = getReportSummary(totalUserCount, validReports);

  useEffect(() => {
    setIsPostActive(post.isPostVisible);
  }, [post.isPostVisible]);

  // Flips at once, flips back if the save didn't happen — the switch used to
  // stay on the new state even when the server refused it.
  const handleChangePostStatus = async (postId) => {
    if (isSavingStatus) return;
    const previous = isPostActive;
    const nextActive = !previous;
    setIsPostActive(nextActive);
    setIsSavingStatus(true);
    try {
      const saved = await onStatusSuccess(postId, nextActive);
      if (saved === false) setIsPostActive(previous);
    } catch (error) {
      console.error("handleChangePostStatus catch error::", error);
      setIsPostActive(previous);
    } finally {
      setIsSavingStatus(false);
    }
  };

  const handleEllipsisClick = (e) => {
    e.stopPropagation();
    setIsReportsModalOpen(true);
  };

  // Prefer the post's tags; fall back to its genres for posts saved before tags.
  const chips = (Array.isArray(post.tags) && post.tags.length > 0)
    ? post.tags.map((t) => ({ label: t.tagName }))
    : (post.category || []).map((c) => ({ label: c.label }));
  const firstCategoryName = chips[0]?.label;
  const allCategories = chips.slice(1);
  const isVideo = post.mediaType === "video";
  const visibilityLabelId = `post-visibility-${post._id}`;

  const handleMediaKeyDown = (e) => {
    if (!onClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(post);
    }
  };

  return (
    <article className={styles.postCard}>
      <div
        className={styles.postCardMedia}
        data-interactive={onClick ? "true" : undefined}
        onClick={() => onClick?.(post)}
        onKeyDown={handleMediaKeyDown}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        aria-label={onClick ? "Open post details" : undefined}
      >
        {post.mediaType === "image" ? (
          <img
            className={styles.image}
            src={post.posts?.uploadedImageUrl ? convertToCloudFrontUrl(post.posts.uploadedImageUrl) : noImageUrl}
            alt={onClick ? "" : "Post thumbnail"}
          />
        ) : post.mediaType === "video" ? (
          <video
            controls
            disablePictureInPicture
            controlsList="nodownload"
            autoPlay
            muted
            className={styles.image}
          >
            <source src={post.reels?.uploadedImageUrl ? convertToCloudFrontUrl(post.reels.uploadedImageUrl) : undefined} type="video/mp4" />
          </video>
        ) : (
          post.mediaType === "guide" && post.carouselImages?.length > 0 && (
            <img
              className={styles.image}
              src={post.carouselImages[0]?.image?.uploadedImageUrl ? convertToCloudFrontUrl(post.carouselImages[0].image.uploadedImageUrl) : noImageUrl}
              alt={onClick ? "" : "Guide thumbnail"}
            />
          )
        )}
        {isVideo && (
          <button
            type="button"
            className={styles.videoEllipsisBtn}
            onClick={handleEllipsisClick}
            aria-label="View report details"
            title="Report details"
          >
            <MoreVertical size={20} strokeWidth={2} aria-hidden />
          </button>
        )}
        <div className={styles.mediaOverlay} aria-hidden />
        <div className={styles.postCardActions}>
          <button
            type="button"
            className={styles.iconActionBtn}
            onClick={(e) => {
              e.stopPropagation();
              onEditSuccess(post);
            }}
            aria-label="Edit post"
            title="Edit post"
          >
            <Pencil size={18} strokeWidth={2} aria-hidden />
          </button>
          <button
            type="button"
            className={`${styles.iconActionBtn} ${styles.iconActionBtnDanger}`}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteSuccess(post._id);
            }}
            aria-label="Delete post"
            title="Delete post"
          >
            <Trash2 size={18} strokeWidth={2} aria-hidden />
          </button>
        </div>
      </div>

      {post.title ? (
        <h6 className={styles.postCardTitle} title={post.title}>{post.title}</h6>
      ) : null}

      <footer className={styles.postCardFooter}>
        <div
          className={styles.categoryBlock}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div className={styles.categoryBadges}>
            <span className={styles.categoryBadgePrimary} title={firstCategoryName}>{firstCategoryName}</span>
            {allCategories.length > 0 && (
              <span className={styles.categoryBadgeMore} aria-hidden>+{allCategories.length}</span>
            )}
          </div>
          {showTooltip && allCategories.length > 0 && (
            <div className={styles.categoryTooltip} role="tooltip">
              <div className={styles.tooltipArrow} />
              <div className={styles.tooltipContent}>
                {allCategories.map((category, index) => (
                  <span key={index} className={styles.tooltipCategory}>{category.label}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.addedByBlock}>
          <AddedBy record={post} />
        </div>

        <div className={styles.visibilityBlock}>
          <button
            type="button"
            role="switch"
            aria-checked={isPostActive}
            aria-busy={isSavingStatus}
            disabled={isSavingStatus}
            aria-labelledby={visibilityLabelId}
            className={styles.statusSwitch}
            onClick={(e) => {
              e.stopPropagation();
              handleChangePostStatus(post._id);
            }}
            title={isPostActive ? "Set post hidden" : "Set post visible"}
          >
            <span className={styles.statusSwitchTrack} aria-hidden>
              <span className={styles.statusSwitchThumb} />
            </span>
          </button>
        </div>
      </footer>

      <Modal
        isOpen={isReportsModalOpen}
        onRequestClose={() => setIsReportsModalOpen(false)}
        className={styles.reportsReactModalContent}
        overlayClassName={styles.reportsReactModalOverlay}
        contentLabel="Report details"
        aria-modal="true"
      >
        {validReports.length > 0 ? (
          <div className={styles.reportsModal}>
            <div className={styles.reportsModalHeader}>
              <div className={styles.reportsModalHeaderText}>
                <h5 className={styles.reportsModalTitle}>Reporter Details</h5>
                <p className={styles.reportsModalSummary} role="status">
                  {totalUserCount != null && totalUserCount > 0 ? (
                    <>
                      Out of{" "}
                      <strong className={styles.reportsModalSummaryEm}>{totalUserCount}</strong>{" "}
                      {totalUserCount === 1 ? "user" : "users"},{" "}
                      <strong className={styles.reportsModalSummaryEm}>{reportsCount}</strong>{" "}
                      {reportsCount === 1 ? "user" : "users"} reported this content.
                    </>
                  ) : (
                    <>
                      <strong className={styles.reportsModalSummaryEm}>{reportsCount}</strong>{" "}
                      {reportsCount === 1 ? "user has" : "users have"} reported this content.
                    </>
                  )}
                </p>
              </div>
              <button
                type="button"
                className={styles.reportsModalClose}
                onClick={() => setIsReportsModalOpen(false)}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className={styles.reportsModalStats}>
              <div className={styles.reportsStatCard}>
                <Users className={styles.reportsStatIcon} size={18} aria-hidden />
                <div>
                  <span className={styles.reportsStatLabel}>Total Users</span>
                  <span className={styles.reportsStatValue}>
                    {totalUserCount != null ? totalUserCount : "—"}
                  </span>
                </div>
              </div>
              <div className={`${styles.reportsStatCard} ${styles.reportsStatCardAccent}`}>
                <Flag className={styles.reportsStatIcon} size={18} aria-hidden />
                <div>
                  <span className={styles.reportsStatLabel}>Reports</span>
                  <span className={styles.reportsStatValue}>{reportsCount > 0 ? reportsCount : "—"}</span>
                </div>
              </div>
            </div>

            <div className={styles.reportsModalBody}>
              <div className={styles.reportsTableDesktop}>
                <div className={styles.reportsTableCard}>
                  <div className={styles.reportsTableWrapper}>
                    <table className={styles.reportsTable}>
                      <thead>
                        <tr>
                          <th className={styles.reportsThIndex}>#</th>
                          <th>User name</th>
                          <th>Email</th>
                          <th>Reason</th>
                          <th className={styles.reportsThDate}>Reported at</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validReports.map((report, index) => (
                          <tr key={report.userId?.concat(String(report.reportedAt)) || index}>
                            <td className={styles.reportsTdIndex}>{index + 1}</td>
                            <td><span className={styles.reportsCellName}>{report.reportedBy || "—"}</span></td>
                            <td><span className={styles.reportsCellEmail}>{report.reportedEmail || "—"}</span></td>
                            <td><span className={styles.reportsCellReason}>{report.reason || "—"}</span></td>
                            <td className={styles.reportsTdDate}>{formatReportDate(report.reportedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <ul className={styles.reportsMobileList} aria-label="Report list">
                {validReports.map((report, index) => (
                  <li key={report.userId?.concat(String(report.reportedAt)) || index} className={styles.reportsMobileCard}>
                    <div className={styles.reportsMobileCardHeader}>
                      <span className={styles.reportsMobileIndex}>#{index + 1}</span>
                      <span className={styles.reportsMobileDate}>{formatReportDate(report.reportedAt)}</span>
                    </div>
                    <dl className={styles.reportsMobileFields}>
                      <div className={styles.reportsMobileRow}>
                        <dt>User name</dt>
                        <dd>{report.reportedBy || "—"}</dd>
                      </div>
                      <div className={styles.reportsMobileRow}>
                        <dt>Email</dt>
                        <dd className={styles.reportsMobileEmail}>{report.reportedEmail || "—"}</dd>
                      </div>
                      <div className={styles.reportsMobileRow}>
                        <dt>Reason</dt>
                        <dd>{report.reason || "—"}</dd>
                      </div>
                    </dl>
                  </li>
                ))}
              </ul>

            </div>
          </div>
        ) : (
          <div className={styles.reportsEmpty}>
            <div className={styles.reportsEmptyIcon}>
              <Flag size={36} strokeWidth={1.5} aria-hidden />
            </div>
            <p className={styles.reportsEmptyText}>No reports available for this post.</p>
          </div>
        )}
      </Modal>
    </article>
  );
};

export default PostCard;
